import React, { useState, useEffect, useRef, useCallback } from "react";
import * as SignalR from "@aspnet/signalr";
import { url } from "../constants/config";
import { useSelector } from "react-redux";
import * as kurentoUtils from "kurento-utils";

const useMedia = roomId => {
  const connection = useRef(null);

  const [status, setStatus] = useState("");

  const mediaSessions = useRef([]);

  const [mediaElements, setMediaElements] = useState([]);

  const [myConnectionId, setMyConnectionId] = useState("");

  const { authToken } = useSelector(state => state.auth);

  const [streaming, setStreaming] = useState(false);

  useEffect(() => {
    if (authToken !== "") {
      setStatus("Connecting");

      let hubConnection = new SignalR.HubConnectionBuilder()
        .withUrl(url + "/mediaHub", { accessTokenFactory: () => authToken })
        .configureLogging(SignalR.LogLevel.Information)
        .build();

      hubConnection
        .start()
        .then(function() {
          connection.current = hubConnection;
          setStatus("Connected");

          hubConnection.on(
            "ExistingMedia",
            (myConnectionId, existingMediaSessions) => {
              //console.log("existing media: ", existingMediaSessions);
              setMyConnectionId(myConnectionId);
              createInboundMediaSessions(existingMediaSessions);
            }
          );

          hubConnection.on("NewMedia", mediaSession => {
            createInboundMediaSessions([mediaSession]);
          });

          hubConnection.on("IceCandidate", (candidate, connectionId) => {
            mediaSessions.current[connectionId].rtcPeer.addIceCandidate(
              candidate,
              function(error) {
                if (error) {
                  console.error("Error adding candidate: " + error);
                  return;
                }
              }
            );
          });

          hubConnection.on("ReceiveVideoAnswer", (sdpAnswer, connectionId) => {
            mediaSessions.current[connectionId].rtcPeer.processAnswer(
              sdpAnswer,
              function(error) {
                if (error) return console.error(error);
              }
            );
          });

          hubConnection.on("RemoveMedia", mediaSession => {
            console.log("RemoveMedia: ", mediaSession);
            if (mediaSessions.current[mediaSession.connectionId])
              mediaSessions.current[
                mediaSession.connectionId
              ].rtcPeer.dispose();
            setMediaElements(mediaElements =>
              mediaElements.filter(
                m => m.connectionId !== mediaSession.connectionId
              )
            );
          });

          hubConnection.invoke("JoinRoom", roomId);
        })
        .catch(error => {
          setStatus("Error");
        });

      hubConnection.onclose(error => {
        setStatus("Closed");
      });
    }

    return () => {
      setStatus("Stopping");
      if (connection && connection.current) connection.current.stop();
    };
  }, [authToken, roomId]);

  async function createInboundMediaSessions(inboundMediaSessions) {
    let newMediaElements = [];

    inboundMediaSessions.forEach(mediaSession => {
      //make a ref for each 1
      mediaSession.mediaRef = React.createRef();
      newMediaElements.push({
        connectionId: mediaSession.connectionId,
        identity: mediaSession.identity,
        mediaRef: mediaSession.mediaRef,
        options: mediaSession.mediaOptions
      });

      mediaSessions.current[mediaSession.connectionId] = mediaSession;
    });

    await setMediaElements(el => [...el, ...newMediaElements]);

    //console.log("mediaSessions.current: ", mediaSessions.current);

    //set up web rtc events for each 1
    //for (var id in mediaSessions.current) {
    inboundMediaSessions.forEach(inboundSession => {
      let mediaSession = mediaSessions.current[inboundSession.connectionId];

      console.log("existing mediaSession ref: ", mediaSession.mediaRef);

      let peerOptions = {
        remoteVideo: mediaSession.mediaRef.current,
        onicecandidate: onIceCandidate(mediaSession.connectionId),
        oncandidategatheringdone: () => console.log("done gathering candidates")
      };

      mediaSession.rtcPeer = new kurentoUtils.WebRtcPeer.WebRtcPeerRecvonly(
        peerOptions,
        async function(error) {
          if (error) {
            await setMediaElements(mediaElements =>
              mediaElements.filter(
                m => m.connectionId !== mediaSession.connectionId
              )
            );
            return console.error(error);
          }
          this.generateOffer(offerToReceiveVideo(mediaSession.connectionId));
        }
      );
    });
  }

  const offerToReceiveVideo = connectionId => (error, offerSdp, wp) => {
    console.log("offerToReceiveVideo: ", connectionId);

    if (error) return console.error("sdp offer error");
    connection.current.invoke("ReceiveVideoFrom", connectionId, offerSdp);
  };

  const onIceCandidate = connectionId => (candidate, wp) => {
    console.log("onIceCandidate: ", connectionId);

    connection.current.invoke(
      "OnIceCandidate",
      candidate.candidate,
      candidate.sdpMid,
      candidate.sdpMLineIndex,
      connectionId
    );
  };

  //expose function to start new media
  async function startMedia(audio, video) {
    //check if already streaming
    const constraints = {
      audio: audio,
      video: video
    };

    let me = {
      connectionId: myConnectionId,
      identity: {
        name: "Me"
      },
      mediaRef: React.createRef()
    };

    mediaSessions.current[myConnectionId] = me;

    let newMediaElement = {
      connectionId: me.connectionId,
      identity: me.identity,
      mediaRef: me.mediaRef,
      options: constraints
    };

    await setMediaElements(mediaElements => [
      ...mediaElements,
      newMediaElement
    ]);

    let localOptions = {
      localVideo: newMediaElement.mediaRef.current,
      mediaConstraints: constraints,
      onicecandidate: onIceCandidate(myConnectionId)
    };

    me.rtcPeer = new kurentoUtils.WebRtcPeer.WebRtcPeerSendonly(
      localOptions,
      async function(error) {
        if (error) {
          setMediaElements(mediaElements =>
            mediaElements.filter(m => m.connectionId !== me.connectionId)
          );
          return console.error(error);
        }
        setStreaming(true);

        await connection.current.invoke("SignalMedia", {
          Video: constraints.video ? true : false,
          audio: constraints.audio ? true : false
        });

        this.generateOffer(offerToReceiveVideo(me.connectionId));
      }
    );
  }

  //expose function to stop media
  function stopMedia() {
    connection.current.invoke("DestroyMedia");
    setStreaming(false);
  }

  return [{ status, mediaElements, streaming }, startMedia, stopMedia];
};

export default useMedia;
